import React, { useState, useCallback, useEffect } from 'react';
import { Table, Input, Button, Space, Card, Typography, Dropdown, Tag, Grid } from "antd";
import { SearchOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getTransactions } from '../TransactionsServices';
import { debounce } from 'lodash';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useTranslation } from 'react-i18next';
import './TransactionTable.css';

const { Title } = Typography;
const { useBreakpoint } = Grid;


const TransactionTable = ({ onAction, onShowAddTransactionModal, refreshTable, onTransactionsLoaded, externalTransactions }) => {
    const { user, loading, setLoading } = useGlobal();
    const { t } = useTranslation();

    const screens = useBreakpoint();
    const [data, setData] = useState([]);
    const { handleError } = useErrorHandler();
    const [searchString, setSearchString] = useState('');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sort_by, setSortBy] = useState(undefined);
    const [sort_direction, setSortDirection] = useState(undefined);
    const [filters, setFilters] = useState({
        transaction_type: undefined,
        transaction_status: undefined,
        user_name: undefined,
    });
    const [pagination, setPagination] = useState({
        total: 0,
        last: false,
    });

    // Use external transactions if provided
    useEffect(() => {
        if (externalTransactions && externalTransactions.length > 0) {
            setData(externalTransactions);
        }
    }, [externalTransactions]);

    const fetchTransactions = useCallback(async (search_string) => {
        try {
            setLoading(true);
            const response = await getTransactions({ page, size: pageSize, sort_by, sort_direction, filters: filters, search_string }, user.is_admin);
            setData(response.data);
            // Call the onTransactionsLoaded callback if provided
            if (onTransactionsLoaded) {
                onTransactionsLoaded(response.data);
            }
            setPagination({
                total: response.metadata.total,
                last: response.metadata.last,
            });
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, page, pageSize, sort_by, sort_direction, filters, user.is_admin, onTransactionsLoaded]);

    useEffect(() => {
        fetchTransactions();
    }, [fetchTransactions, refreshTable]);

    const handleTableChange = (pagination, filters, sorter) => {
        const { order, field } = sorter || {};
        const sort_by = field;
        const sort_direction = order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined;
        if (pagination.current) {
            setPage(pagination.current);
        }
        if (pagination.pageSize) {
            setPageSize(pagination.pageSize);
        }
        if (sort_by) {
            setSortBy(sort_by);
        }
        if (sort_direction) {
            setSortDirection(sort_direction);
        }
        if (filters) {
            const statusFilters = {};
            if (filters.transaction_type) {
                statusFilters.transaction_type = filters.transaction_type;
            }
            if (filters.transaction_status) {
                statusFilters.transaction_status = filters.transaction_status;
            }
            setFilters(statusFilters);
            setPage(1);
        }
    };

    const handleFilterChange = (value, key) => {
        setFilters({
            ...filters,
            [key]: value,
        });
    };

    const debouncedFetchTransactions = useCallback(
        debounce((search) => {
            fetchTransactions(search);
        }, 500),
        [fetchTransactions]
    );

    const handleSearch = (e) => {
        setSearchString(e.target.value);
        debouncedFetchTransactions(e.target.value);
    };

    const actionsMenu = (record) => {
        return (
            {
                items:
                    [
                        {
                            key: 'view',
                            label: t('view_details'),
                        },
                        // Only show reconcile option for payment transactions
                        ...(record.transaction_type === 'payment' ? [{
                            key: 'reconcile',
                            label: t('reconcile_transaction'),
                        }] : []),
                        ...(user?.is_admin ? !record?.is_disabled ? [{
                            key: 'disable',
                            label: t('disable_transaction'),
                        }] : [{
                            key: 'enable',
                            label: t('enable_transaction'),
                        }] : []),
                    ],
                onClick: ({ key }) => onAction(key, record)
            }
        );
    };

    const columns = [
        {
            ...(user?.is_admin ? [{
                title: t('user'),
                dataIndex: "user",
                key: "user",
                sorter: true,
                render: (user) => (
                    user?.full_name
                ),
            }] : []),
        },
        {
            title: t('transaction_type'),
            dataIndex: "transaction_type",
            key: "transaction_type",
            sorter: true,
            filters: [
                { text: t('payment'), value: 'payment' },
                { text: t('message'), value: 'message' },
                { text: t('admin'), value: 'admin' },
                { text: t('system'), value: 'system' },
            ],
            render: (type) => {
                return (
                    <Tag color={type === "payment" ? "green" : type === "refund" ? "red" : "blue"}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Tag>
                );
            }
        },
        {
            title: t('transaction_status'),
            dataIndex: "transaction_status",
            key: "transaction_status",
            sorter: true,
            filters: [
                { text: t('pending'), value: 'pending' },
                { text: t('completed'), value: 'completed' },
                { text: t('cancelled'), value: 'cancelled' },
            ],
            render: (status) => {
                let color;
                switch (status) {
                    case "completed":
                        color = "success";
                        break;
                    case "pending":
                        color = "warning";
                        break;
                    case "cancelled":
                        color = "error";
                        break;
                    case "disabled":
                        color = "default";
                        break;
                    default:
                        color = "default";
                }
                return <Tag color={color}>{status.charAt(0).toUpperCase() + status.slice(1)}</Tag>;
            }
        },
        {
            title: t('coins_amount'),
            dataIndex: "coins_amount",
            key: "coins_amount",
            sorter: true,
            render: (amount) => {
                const isPositive = amount > 0;
                return (
                    <span style={{ color: isPositive ? "green" : "red" }}>
                        {amount}
                    </span>
                );
            }
        },
        {
            title: t('description'),
            dataIndex: "description",
            key: "description",
            sorter: true,
        },
        {
            title: t('cancellation_reason'),
            dataIndex: "cancellation_reason",
            key: "cancellation_reason",
            sorter: true,
        },
        {
            title: t('created_at'),
            dataIndex: "created_at",
            key: "created_at",
            sorter: true,
        },
        {
            title: t('disabling_reason'),
            dataIndex: "disabling_reason",
            key: "disabling_reason",
            sorter: true,
        },
        {
            title: t('actions'),
            key: "actions",
            render: (text, record) => (
                <Dropdown menu={actionsMenu(record)} trigger={['click']}>
                    <Button icon={<EllipsisOutlined />} />
                </Dropdown>
            ),
        }
    ];

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Card title={t('transactions')} extra={
                <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={onShowAddTransactionModal}
                >
                    {t('add_new_transaction')}
                </Button>
            }
                styles={{
                    body: {
                        padding: 16
                    }
                }} style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>

                <Input
                    placeholder={t('search')}
                    value={searchString}
                    onChange={handleSearch}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    style={{ 
                        width: isInputFocused ? '100%' : (screens.xs ? '100%' : '300px'),
                        transition: 'width 0.4s ease-in-out'
                    }}
                    prefix={<SearchOutlined />}
                />
            </Card>
            <Card style={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>

                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 800 }}
                    pagination={{
                        ...pagination,
                        size: 'default',
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '25', '50', '100'],
                    }}
                    onChange={handleTableChange}
                    rowClassName={(record) => record.is_disabled ? 'disabled-row' : ''}
                />
            </Card>
        </Space>
    );
};

export default TransactionTable;
